import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import {
	LIBRARY_TYPE_IN_PERSON_TRAINING,
	LIBRARY_TYPE_INTELLIGUIDE,
	LIBRARY_TYPE_LEARNING_HABIT,
	LIBRARY_TYPE_MANUAL,
	LIBRARY_TYPE_POLICY,
	LIBRARY_TYPE_SOP,
	LIBRARY_TYPE_TRAINING,
	LIBRARY_TYPE_WEBINAR,
} from "../../models/Constants";
import Loading from "../../theme/partials/Loading";
import { FileHandlerProvider } from "../../theme/partials/FileHandlerProvider";
import UserThreadsConversations from "./libraries/library-assistant/UserThreadsConversations";

const Libraries = lazy(() => import("./libraries/Libraries"));
const LibraryDetails = lazy(() => import("./libraries/LibraryDetails"));
const CompanySettings = lazy(() => import("./settings/CompanySettings"));
const UserProfile = lazy(() => import("./user_profile/UsersProfile"));
const CourseSalesPage = lazy(() => import("./reports/CourseSalesPage"));
const AdminDashboard = lazy(() => import("../admin-panel/AdminDashboard"));
const StudentDashboard = lazy(() => import("./StudentDashboard"));
const LearningHabits = lazy(() => import("./learning-habits/LearningHabits"));
const ActionStepsDashboard = lazy(() => import("./ActionStepsDashboard"));
const LearningHabitStats = lazy(() => import("./learning-habits/LearningHabitStats"));
const AIChatMain = lazy(() => import("./libraries/AIChatMain"));
const LibraryPageRestriction = lazy(() =>
	import("../PermissionProvider").then((module) => ({
		default: module.LibraryPageRestriction,
	}))
);

const AIChatUsers = lazy(() => import("./libraries/library-assistant/AIChatUsers"));
const CompanyOverview = lazy(() => import("./settings/CompanyOverview"));
const AISettings = lazy(() => import("./settings/AISettings"));
const AICompanySettings = lazy(() => import("./settings/AICompanySettings"));

const PeopleAccess = lazy(() => import("./settings/company-settings/PeopleAccess"));
const AppData = lazy(() => import("./settings/company-settings/AppData"));
const Organization = lazy(() => import("./settings/company-settings/Organization"));
const SafetyPrivacy = lazy(() => import("./settings/company-settings/SafetyPrivacy"));

export const URL_LIBRARY_TRAINING = "trainings";
export const URL_LIBRARY_IN_PERSON_TRAINING = "in-person-trainings";
export const URL_LIBRARY_WEBINARS = "webinars";
export const URL_LIBRARY_INTELLIGUIDE = "company_apps";
export const URL_LIBRARY_SOP = "sops";
export const URL_LIBRARY_LEARNING_HABITS = "learning-habits";

const LibraryPageRestrictionRouteWwrapper = () => {
	return (
		<Suspense fallback={<Loading />}>
			<Outlet />
		</Suspense>
	);
};

export default function DashboardRoutes() {
	return (
		<Suspense fallback={<Loading />}>
			<Routes>
				<Route path="/">
					<Route index element={<Navigate to="trainings" replace />} />
					<Route element={<LibraryPageRestrictionRouteWwrapper />}>
						<Route path="trainings" element={<Libraries type={LIBRARY_TYPE_TRAINING} />} />
						<Route
							path="trainings/:library_code"
							element={<LibraryDetails type={LIBRARY_TYPE_TRAINING} />}
						/>
						<Route path="manuals" element={<Libraries type={LIBRARY_TYPE_MANUAL} />} />
						<Route path="manuals/:library_code" element={<LibraryDetails type={LIBRARY_TYPE_MANUAL} />} />
						<Route path="policies" element={<Libraries type={LIBRARY_TYPE_POLICY} />} />
						<Route path="policies/:library_code" element={<LibraryDetails type={LIBRARY_TYPE_POLICY} />} />
						<Route
							path="in-person-trainings"
							element={<Libraries type={LIBRARY_TYPE_IN_PERSON_TRAINING} />}
						/>
						<Route
							path="in-person-trainings/:library_code"
							element={<LibraryDetails type={LIBRARY_TYPE_IN_PERSON_TRAINING} />}
						/>
						<Route path="webinars" element={<Libraries type={LIBRARY_TYPE_WEBINAR} />} />
						<Route path="webinars/:library_code" element={<LibraryDetails type={LIBRARY_TYPE_WEBINAR} />} />

						<Route path="company_apps" element={<Libraries type={LIBRARY_TYPE_INTELLIGUIDE} />} />
						<Route
							path="company_apps/:library_code"
							element={<LibraryDetails type={LIBRARY_TYPE_INTELLIGUIDE} />}
						/>
						<Route path={URL_LIBRARY_SOP}>
							<Route path="" element={<Libraries type={LIBRARY_TYPE_SOP} />} />
							<Route path=":library_code" element={<LibraryDetails type={LIBRARY_TYPE_SOP} />} />
						</Route>
					</Route>
					<Route path="learning-habits" element={<LearningHabits type={LIBRARY_TYPE_LEARNING_HABIT} />} />
					<Route
						path="learning-habits/:learningHabitCode"
						element={<LearningHabits type={LIBRARY_TYPE_LEARNING_HABIT} />}
					/>
					<Route path="learning-habits/lessons" element={<Libraries type={LIBRARY_TYPE_LEARNING_HABIT} />} />
					<Route
						path="learning-habits/lessons/:library_code"
						element={<LibraryDetails type={LIBRARY_TYPE_LEARNING_HABIT} />}
					/>
					<Route path="learning-habits/statistics/:learningHabitCode" element={<LearningHabitStats />} />
					{/* <Route path="settings" element={<CompanySettings />} /> */}
					<Route path="settings">
						<Route index element={<CompanySettings />} />
						<Route path="people-access" element={<PeopleAccess />} />
						<Route path="apps-data" element={<AppData />} />
						<Route path="organization" element={<Organization />} />
						<Route path="safety-privacy" element={<SafetyPrivacy />} />
					</Route>
					<Route path="user-profile" element={<UserProfile />} />
					<Route path="course-sales" element={<CourseSalesPage />} />
					<Route path="admin-dashboard" element={<AdminDashboard />} />
					<Route path="student-dashboard" element={<StudentDashboard />} />
					<Route path="student-action-steps" element={<ActionStepsDashboard />} />

					<Route path="ai-chat-users" element={<AIChatUsers />} />
					<Route path="ai-chat-thread-conversations/:user_id/:thread_id" element={<UserThreadsConversations />} />
					<Route path="ai-chat">
						<Route
							path=":thread_id?"
							element={<FileHandlerProvider><AIChatMain type={LIBRARY_TYPE_WEBINAR} /></FileHandlerProvider>}
						/>
						<Route path="settings" element={<AICompanySettings />} />
						<Route path="company-enrichment" element={<CompanyOverview />} />
						<Route path="company-settings" element={<AISettings />} />
					</Route>
				</Route>
			</Routes>
		</Suspense>
	);
}
